import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  Unsubscribe,
  writeBatch
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '@/lib/firebase'
import Fuse from 'fuse.js'
import { EarningsService } from './earningsService'
import { CompensationService } from './compensationService'
import { PeriodService } from './periodService'
import {
  DriverMaster,
  Alias,
  CreateDriverMasterData,
  UpdateDriverMasterData,
  CreateAliasData,
  DriverMasterValidationError,
  AliasValidationError,
  DriverMergeOptions,
  DriverMergePreview
} from '@/types/driverMaster'

// Validation utilities
export class DriverMasterValidator {
  static validateDriverMaster(data: CreateDriverMasterData | UpdateDriverMasterData): DriverMasterValidationError[] {
    const errors: DriverMasterValidationError[] = []

    // Validate primaryName if provided
    if ('primaryName' in data) {
      if (!data.primaryName || data.primaryName.trim().length === 0) {
        errors.push({ field: 'primaryName', message: 'Förarens namn är obligatoriskt' })
      } else if (data.primaryName.trim().length < 2) {
        errors.push({ field: 'primaryName', message: 'Förarens namn måste vara minst 2 tecken' })
      }
    }

    // Validate email if provided
    if (data.email && data.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(data.email)) {
        errors.push({ field: 'email', message: 'Ogiltigt e-postformat' })
      }
    }

    // Validate phone if provided
    if (data.phone && data.phone.trim().length > 0) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,15}$/
      if (!phoneRegex.test(data.phone)) {
        errors.push({ field: 'phone', message: 'Ogiltigt telefonnummer' })
      }
    }

    // Validate tax settings if provided (UpdateDriverMasterData only)
    if ('taxCalculationMethod' in data) {
      const taxMethod = data.taxCalculationMethod
      const taxRate = data.taxRate
      const taxTableName = data.taxTableName
      const taxColumn = data.taxColumn

      // If taxCalculationMethod is set, validate it
      if (taxMethod !== undefined && taxMethod !== null) {
        // Validate method is either 'fixed' or 'table'
        if (taxMethod !== 'fixed' && taxMethod !== 'table') {
          errors.push({ field: 'taxCalculationMethod', message: 'Skatteberäkningsmetod måste vara "fixed" eller "table"' })
        }

        // If method is 'fixed', validate taxRate
        if (taxMethod === 'fixed') {
          if (taxRate === undefined || taxRate === null) {
            errors.push({ field: 'taxRate', message: 'Skattesats är obligatorisk när fast procentsats är vald' })
          } else if (taxRate < 0 || taxRate > 100) {
            errors.push({ field: 'taxRate', message: 'Skattesats måste vara mellan 0 och 100%' })
          }
        }

        // If method is 'table', validate taxTableName and taxColumn
        if (taxMethod === 'table') {
          if (!taxTableName || taxTableName.trim().length === 0) {
            errors.push({ field: 'taxTableName', message: 'Skattetabell är obligatorisk när skattetabell är vald' })
          }
          if (taxColumn === undefined || taxColumn === null) {
            errors.push({ field: 'taxColumn', message: 'Kolumn är obligatorisk när skattetabell är vald' })
          } else if (taxColumn < 1 || taxColumn > 6) {
            errors.push({ field: 'taxColumn', message: 'Kolumn måste vara mellan 1 och 6' })
          }
        }
      }
    }

    return errors
  }

  static validateAlias(alias: CreateAliasData, existingAliases: Alias[] = []): AliasValidationError[] {
    const errors: AliasValidationError[] = []

    // Platform is required
    if (!alias.platform || alias.platform.trim().length === 0) {
      errors.push({ field: 'platform', message: 'Plattform är obligatorisk' })
    }

    // Must have at least one additional field
    const hasAdditionalField = alias.uuid || alias.email || alias.phone || alias.name
    if (!hasAdditionalField) {
      errors.push({ field: 'general', message: 'Minst ett av följande fält måste fyllas i: UUID, E-post, Telefon eller Namn' })
    }

    // Validate email format if provided
    if (alias.email && alias.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(alias.email)) {
        errors.push({ field: 'email', message: 'Ogiltigt e-postformat' })
      }
    }

    // Validate phone format if provided
    if (alias.phone && alias.phone.trim().length > 0) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,15}$/
      if (!phoneRegex.test(alias.phone)) {
        errors.push({ field: 'phone', message: 'Ogiltigt telefonnummer' })
      }
    }

    // Check for duplicate platform
    if (alias.platform && existingAliases.some(a => a.platform.toLowerCase() === alias.platform.toLowerCase())) {
      errors.push({ field: 'platform', message: 'En alias för denna plattform finns redan' })
    }

    return errors
  }
}


// Main Driver Master Service
export class DriverMasterService {
  private static readonly COLLECTION_NAME = 'drivers'

  // Get collection reference for a user
  private static getUserCollection(user: User) {
    return collection(db, 'users', user.uid, this.COLLECTION_NAME)
  }

  // Get document reference for a specific driver
  private static getDriverDocument(user: User, driverId: string) {
    return doc(db, 'users', user.uid, this.COLLECTION_NAME, driverId)
  }

  // Convert Firestore document to DriverMaster
  private static convertFirestoreDoc(doc: any): DriverMaster {
    const data = doc.data()
    return {
      id: doc.id,
      primaryName: data.primaryName,
      aliases: data.aliases || [],
      legacyDriverIds: data.legacyDriverIds || [],
      isActive: data.isActive !== false, // Default to true if not set
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      email: data.email,
      phone: data.phone,
      personalCode: data.personalCode,
      taxiCode: data.taxiCode,
      employeeNumber: data.employeeNumber,
      bankAccount: data.bankAccount,
      paymentReference: data.paymentReference,
      carId: data.carId,
      taxCalculationMethod: data.taxCalculationMethod,
      taxRate: data.taxRate,
      taxTableName: data.taxTableName,
      taxColumn: data.taxColumn,
      salaryGarnishment: data.salaryGarnishment,
      payoutCompanyId: data.payoutCompanyId || undefined,
    }
  }

  // Get all active drivers
  static async getDrivers(user: User): Promise<DriverMaster[]> {
    try {
      const q = query(
        this.getUserCollection(user),
        where('isActive', '==', true),
        orderBy('primaryName', 'asc')
      )
      
      const querySnapshot = await getDocs(q)
      const drivers = querySnapshot.docs.map(doc => this.convertFirestoreDoc(doc))
      
      
      return drivers
    } catch (error) {
      console.error('Error fetching drivers:', error)
      throw new Error('Det gick inte att hämta förare')
    }
  }

  // Get single driver by ID (includes inactive drivers)
  static async getDriver(user: User, id: string): Promise<DriverMaster | null> {
    try {
      const docRef = this.getDriverDocument(user, id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const driver = this.convertFirestoreDoc(docSnap)


      return driver
    } catch (error) {
      console.error('Error fetching driver:', error)
      throw new Error('Det gick inte att hämta föraren')
    }
  }

  // Get single active driver by ID (filters by isActive=true)
  static async getActiveDriver(user: User, id: string): Promise<DriverMaster | null> {
    try {
      const driver = await this.getDriver(user, id)

      if (!driver || !driver.isActive) {
        return null
      }

      return driver
    } catch (error) {
      console.error('Error fetching active driver:', error)
      throw new Error('Det gick inte att hämta föraren')
    }
  }

  // Create new driver
  static async createDriver(user: User, data: CreateDriverMasterData): Promise<DriverMaster> {
    try {
      // Validate input
      const errors = DriverMasterValidator.validateDriverMaster(data)
      if (errors.length > 0) {
        throw new Error(errors[0].message)
      }

      // Create new driver document with auto-generated ID
      const docRef = doc(this.getUserCollection(user))
      const now = serverTimestamp()
      
      const driverData: any = {
        primaryName: data.primaryName.trim(),
        aliases: [],
        legacyDriverIds: [],
        isActive: true,
        createdAt: now,
        updatedAt: now,
      }

      // Add optional fields if they have values
      if (data.email?.trim()) {
        driverData.email = data.email.trim()
      }
      if (data.phone?.trim()) {
        driverData.phone = data.phone.trim()
      }
      if (data.personalCode?.trim()) {
        driverData.personalCode = data.personalCode.trim()
      }
      if (data.taxiCode?.trim()) {
        driverData.taxiCode = data.taxiCode.trim()
      }
      if (data.employeeNumber?.trim()) {
        driverData.employeeNumber = data.employeeNumber.trim()
      }
      if (data.bankAccount?.trim()) {
        driverData.bankAccount = data.bankAccount.trim()
      }
      if (data.paymentReference?.trim()) {
        driverData.paymentReference = data.paymentReference.trim()
      }
      if (data.carId?.trim()) {
        driverData.carId = data.carId.trim()
      }
      if (data.taxCalculationMethod) {
        driverData.taxCalculationMethod = data.taxCalculationMethod
      }
      if (data.taxRate !== undefined) {
        driverData.taxRate = data.taxRate
      }
      if (data.taxTableName?.trim()) {
        driverData.taxTableName = data.taxTableName.trim()
      }
      if (typeof data.taxColumn === 'number') {
        driverData.taxColumn = data.taxColumn
      }
      if (typeof data.salaryGarnishment === 'number') {
        driverData.salaryGarnishment = data.salaryGarnishment
      }

      await setDoc(docRef, driverData)
      
      // Get the created driver
      const createdDriver = await this.getDriver(user, docRef.id)
      if (!createdDriver) {
        throw new Error('Det gick inte att skapa föraren')
      }
      
      
      return createdDriver
    } catch (error) {
      console.error('Error creating driver:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att skapa föraren')
    }
  }

  // Update existing driver
  static async editDriver(user: User, id: string, updates: UpdateDriverMasterData): Promise<DriverMaster> {
    try {
      // Validate input
      const errors = DriverMasterValidator.validateDriverMaster(updates)
      if (errors.length > 0) {
        throw new Error(errors[0].message)
      }

      const docRef = this.getDriverDocument(user, id)
      
      // Check if driver exists
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Föraren hittades inte')
      }

      // Prepare update data
      const updateData: any = {
        updatedAt: serverTimestamp()
      }

      if ('primaryName' in updates) {
        updateData.primaryName = updates.primaryName?.trim()
      }
      if ('email' in updates) {
        updateData.email = updates.email?.trim() || null
      }
      if ('phone' in updates) {
        updateData.phone = updates.phone?.trim() || null
      }
      if ('personalCode' in updates) {
        updateData.personalCode = updates.personalCode?.trim() || null
      }
      if ('taxiCode' in updates) {
        updateData.taxiCode = updates.taxiCode?.trim() || null
      }
      if ('employeeNumber' in updates) {
        updateData.employeeNumber = updates.employeeNumber?.trim() || null
      }
      if ('bankAccount' in updates) {
        updateData.bankAccount = updates.bankAccount?.trim() || null
      }
      if ('paymentReference' in updates) {
        updateData.paymentReference = updates.paymentReference?.trim() || null
      }
      if ('carId' in updates) {
        updateData.carId = updates.carId?.trim() || null
      }
      if ('taxCalculationMethod' in updates) {
        updateData.taxCalculationMethod = updates.taxCalculationMethod || null
      }
      if ('taxRate' in updates) {
        updateData.taxRate = updates.taxRate ?? null
      }
      if ('taxTableName' in updates) {
        updateData.taxTableName = updates.taxTableName?.trim() || null
      }
      if ('taxColumn' in updates) {
        updateData.taxColumn = updates.taxColumn || null
      }
      if ('salaryGarnishment' in updates) {
        const value = updates.salaryGarnishment
        updateData.salaryGarnishment = typeof value === 'number' ? value : null
      }
      if ('payoutCompanyId' in updates) {
        updateData.payoutCompanyId = updates.payoutCompanyId || null
      }

      await updateDoc(docRef, updateData)
      
      // Get updated driver
      const updatedDriver = await this.getDriver(user, id)
      if (!updatedDriver) {
        throw new Error('Det gick inte att uppdatera föraren')
      }
      
      
      return updatedDriver
    } catch (error) {
      console.error('Error updating driver:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att uppdatera föraren')
    }
  }

  /**
   * Get count of earnings and compensations in open (unlocked) periods for a driver.
   * Used to warn user before deleting a driver.
   */
  static async getDriverDataInOpenPeriods(
    user: User,
    driverId: string
  ): Promise<{ earningsCount: number; compensationsCount: number }> {
    try {
      // Get earnings for this driver (already filters out deleted)
      const earnings = await EarningsService.getEarningsByDriver(user, driverId)
      // Get compensations for this driver (already filters out deleted)
      const compensations = await CompensationService.getCompensationsByDriver(user, driverId)

      if (earnings.length === 0 && compensations.length === 0) {
        return { earningsCount: 0, compensationsCount: 0 }
      }

      // Get unique periods from both earnings and compensations
      const allPeriods = new Set([
        ...earnings.map(e => e.period),
        ...compensations.map(c => c.period)
      ])

      // Check which periods are locked
      const periodLockStatus = await Promise.all(
        [...allPeriods].map(async period => ({
          period,
          isLocked: await PeriodService.isPeriodLocked(user, period)
        }))
      )
      const lockedPeriods = new Set(
        periodLockStatus.filter(p => p.isLocked).map(p => p.period)
      )

      // Count only data in open (unlocked) periods
      const earningsInOpenPeriods = earnings.filter(e => !lockedPeriods.has(e.period))
      const compensationsInOpenPeriods = compensations.filter(c => !lockedPeriods.has(c.period))

      return {
        earningsCount: earningsInOpenPeriods.length,
        compensationsCount: compensationsInOpenPeriods.length
      }
    } catch (error) {
      console.error('Error getting driver data in open periods:', error)
      throw new Error('Det gick inte att kontrollera förarens data')
    }
  }

  /**
   * Soft delete driver.
   * Optionally also soft-deletes earnings and compensations in open (unlocked) periods.
   * Data in locked periods is preserved (snapshots are self-contained).
   *
   * @param user - Authenticated user
   * @param id - Driver ID to delete
   * @param options - Optional settings
   * @param options.deleteDataInOpenPeriods - If true, soft-delete earnings/compensations in open periods
   * @returns Count of deleted earnings and compensations
   */
  static async deleteDriver(
    user: User,
    id: string,
    options?: { deleteDataInOpenPeriods?: boolean }
  ): Promise<{ deletedEarnings: number; deletedCompensations: number }> {
    try {
      const docRef = this.getDriverDocument(user, id)

      // Check if driver exists
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Föraren hittades inte')
      }

      let deletedEarnings = 0
      let deletedCompensations = 0

      // If requested, soft-delete earnings and compensations in open periods
      if (options?.deleteDataInOpenPeriods) {
        const earningsResult = await EarningsService.deleteEarningsByDriverInOpenPeriods(user, id)
        const compensationsResult = await CompensationService.deleteCompensationsByDriverInOpenPeriods(user, id)

        deletedEarnings = earningsResult.deleted
        deletedCompensations = compensationsResult.deleted
      }

      // Soft delete the driver
      await updateDoc(docRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      })

      return { deletedEarnings, deletedCompensations }
    } catch (error) {
      console.error('Error deleting driver:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att ta bort föraren')
    }
  }

  // Create driver alias
  static async createDriverAlias(user: User, driverId: string, alias: CreateAliasData): Promise<DriverMaster> {
    try {
      const docRef = this.getDriverDocument(user, driverId)
      
      // Get existing driver
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Föraren hittades inte')
      }

      const driverData = existingDoc.data()
      const existingAliases = driverData.aliases || []

      // Validate new alias
      const errors = DriverMasterValidator.validateAlias(alias, existingAliases)
      if (errors.length > 0) {
        throw new Error(errors[0].message)
      }

      // Create new alias object (avoid undefined values for Firestore)
      const newAlias: Alias = {
        platform: alias.platform.trim(),
      }

      // Only add fields that have actual values (not undefined)
      if (alias.uuid?.trim()) {
        newAlias.uuid = alias.uuid.trim()
      }
      if (alias.email?.trim()) {
        newAlias.email = alias.email.trim()
      }
      if (alias.phone?.trim()) {
        newAlias.phone = alias.phone.trim()
      }
      if (alias.name?.trim()) {
        newAlias.name = alias.name.trim()
      }

      // Update driver with new alias
      const updatedAliases = [...existingAliases, newAlias]
      
      await updateDoc(docRef, {
        aliases: updatedAliases,
        updatedAt: serverTimestamp()
      })
      
      // Get updated driver
      const updatedDriver = await this.getDriver(user, driverId)
      if (!updatedDriver) {
        throw new Error('Det gick inte att lägga till alias')
      }
      
      
      return updatedDriver
    } catch (error) {
      console.error('Error creating driver alias:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att lägga till alias')
    }
  }

  // Delete driver alias
  static async deleteDriverAlias(user: User, driverId: string, aliasIndex: number): Promise<DriverMaster> {
    try {
      const docRef = this.getDriverDocument(user, driverId)
      
      // Get existing driver
      const existingDoc = await getDoc(docRef)
      if (!existingDoc.exists()) {
        throw new Error('Föraren hittades inte')
      }

      const driverData = existingDoc.data()
      const existingAliases = driverData.aliases || []

      if (aliasIndex < 0 || aliasIndex >= existingAliases.length) {
        throw new Error('Alias hittades inte')
      }

      // Remove alias at index
      const updatedAliases = existingAliases.filter((_: any, index: number) => index !== aliasIndex)
      
      await updateDoc(docRef, {
        aliases: updatedAliases,
        updatedAt: serverTimestamp()
      })
      
      // Get updated driver
      const updatedDriver = await this.getDriver(user, driverId)
      if (!updatedDriver) {
        throw new Error('Det gick inte att ta bort alias')
      }
      
      
      return updatedDriver
    } catch (error) {
      console.error('Error deleting driver alias:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att ta bort alias')
    }
  }

  // Search driver aliases for exact matches
  static async searchDriversAliases(
    user: User,
    platform: string,
    identifiers: { uuid?: string; email?: string; phone?: string; name?: string; personalCode?: string; taxiCode?: string; employeeNumber?: string }
  ): Promise<{ driverId: string; driverName: string; matchType: 'uuid' | 'name' | 'email' | 'phone' | 'personalCode' | 'taxiCode' | 'employeeNumber'; matchedVia: 'alias' | 'primary_name'; matchedValue: string; aliasExists: boolean; needsAlias: boolean; matchScore?: number; fuzzyMatch?: boolean } | null> {
    try {
      // Get all active drivers
      const drivers = await this.getDrivers(user)
      
      // Priority 1: Check UUID matches first across ALL drivers
      if (identifiers.uuid && identifiers.uuid.trim()) {
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias => 
            alias.platform.toLowerCase() === platform.toLowerCase()
          )

          for (const alias of driver.aliases) {
            if (alias.platform.toLowerCase() === platform.toLowerCase() && 
                alias.uuid && 
                identifiers.uuid.trim().toLowerCase() === alias.uuid.trim().toLowerCase()) {
              return { 
                driverId: driver.id, 
                driverName: driver.primaryName,
                matchType: 'uuid',
                matchedVia: 'alias',
                matchedValue: alias.uuid,
                aliasExists: true,
                needsAlias: false,
                matchScore: 100,
                fuzzyMatch: false
              }
            }
          }
        }
      }

      // Priority 2: Check personalCode matches across ALL drivers
      if (identifiers.personalCode && identifiers.personalCode.trim()) {
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias =>
            alias.platform.toLowerCase() === platform.toLowerCase()
          )

          for (const alias of driver.aliases) {
            if (alias.platform.toLowerCase() === platform.toLowerCase() &&
                alias.personalCode &&
                identifiers.personalCode.trim().toLowerCase() === alias.personalCode.trim().toLowerCase()) {
              return {
                driverId: driver.id,
                driverName: driver.primaryName,
                matchType: 'personalCode',
                matchedVia: 'alias',
                matchedValue: alias.personalCode,
                aliasExists: true,
                needsAlias: false,
                matchScore: 100,
                fuzzyMatch: false
              }
            }
          }
        }
      }

      // Priority 3: Check taxiCode matches across ALL drivers
      if (identifiers.taxiCode && identifiers.taxiCode.trim()) {
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias =>
            alias.platform.toLowerCase() === platform.toLowerCase()
          )

          for (const alias of driver.aliases) {
            if (alias.platform.toLowerCase() === platform.toLowerCase() &&
                alias.taxiCode &&
                identifiers.taxiCode.trim().toLowerCase() === alias.taxiCode.trim().toLowerCase()) {
              return {
                driverId: driver.id,
                driverName: driver.primaryName,
                matchType: 'taxiCode',
                matchedVia: 'alias',
                matchedValue: alias.taxiCode,
                aliasExists: true,
                needsAlias: false,
                matchScore: 100,
                fuzzyMatch: false
              }
            }
          }
        }
      }

      // Priority 4: Check employeeNumber matches across ALL drivers
      if (identifiers.employeeNumber && identifiers.employeeNumber.trim()) {
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias =>
            alias.platform.toLowerCase() === platform.toLowerCase()
          )

          for (const alias of driver.aliases) {
            if (alias.platform.toLowerCase() === platform.toLowerCase() &&
                alias.employeeNumber &&
                identifiers.employeeNumber.trim().toLowerCase() === alias.employeeNumber.trim().toLowerCase()) {
              return {
                driverId: driver.id,
                driverName: driver.primaryName,
                matchType: 'employeeNumber',
                matchedVia: 'alias',
                matchedValue: alias.employeeNumber,
                aliasExists: true,
                needsAlias: false,
                matchScore: 100,
                fuzzyMatch: false
              }
            }
          }
        }
      }

      // Priority 5: Check email matches across ALL drivers
      if (identifiers.email && identifiers.email.trim()) {
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias => 
            alias.platform.toLowerCase() === platform.toLowerCase()
          )

          for (const alias of driver.aliases) {
            if (alias.platform.toLowerCase() === platform.toLowerCase() && 
                alias.email && 
                identifiers.email.trim().toLowerCase() === alias.email.trim().toLowerCase()) {
              return { 
                driverId: driver.id, 
                driverName: driver.primaryName,
                matchType: 'email',
                matchedVia: 'alias',
                matchedValue: alias.email,
                aliasExists: true,
                needsAlias: false,
                matchScore: 100,
                fuzzyMatch: false
              }
            }
          }
        }
      }

      // Priority 6: Check phone matches across ALL drivers
      if (identifiers.phone && identifiers.phone.trim()) {
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias => 
            alias.platform.toLowerCase() === platform.toLowerCase()
          )

          for (const alias of driver.aliases) {
            if (alias.platform.toLowerCase() === platform.toLowerCase() && 
                alias.phone && 
                identifiers.phone.trim().replace(/\s/g, '') === alias.phone.trim().replace(/\s/g, '')) {
              return { 
                driverId: driver.id, 
                driverName: driver.primaryName,
                matchType: 'phone',
                matchedVia: 'alias',
                matchedValue: alias.phone,
                aliasExists: true,
                needsAlias: false,
                matchScore: 100,
                fuzzyMatch: false
              }
            }
          }
        }
      }

      // Priority 7: Check exact name matches in aliases
      if (identifiers.name && identifiers.name.trim()) {
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias => 
            alias.platform.toLowerCase() === platform.toLowerCase()
          )

          for (const alias of driver.aliases) {
            if (alias.platform.toLowerCase() === platform.toLowerCase() && 
                alias.name && 
                identifiers.name.trim().toLowerCase() === alias.name.trim().toLowerCase()) {
              return { 
                driverId: driver.id, 
                driverName: driver.primaryName,
                matchType: 'name',
                matchedVia: 'alias',
                matchedValue: alias.name,
                aliasExists: true,
                needsAlias: false,
                matchScore: 100,
                fuzzyMatch: false
              }
            }
          }
        }
      }

      // Priority 5: Check exact name matches against primary names
      if (identifiers.name && identifiers.name.trim()) {
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias => 
            alias.platform.toLowerCase() === platform.toLowerCase()
          )

          if (identifiers.name.trim().toLowerCase() === driver.primaryName.trim().toLowerCase()) {
            return { 
              driverId: driver.id, 
              driverName: driver.primaryName,
              matchType: 'name',
              matchedVia: 'primary_name',
              matchedValue: driver.primaryName,
              aliasExists: hasAliasForPlatform,
              needsAlias: !hasAliasForPlatform,
              matchScore: 100,
              fuzzyMatch: false
            }
          }
        }
      }
      
      // If no exact match found and we have a name, try fuzzy matching
      if (identifiers.name && identifiers.name.trim()) {
        const searchName = identifiers.name.trim()
        
        // Create a list of all searchable names with their associated driver info
        const searchableNames: Array<{
          name: string
          driverId: string
          driverName: string
          matchedVia: 'alias' | 'primary_name'
          aliasExists: boolean
          needsAlias: boolean
          platform?: string
        }> = []
        
        // Add all aliases for the platform
        for (const driver of drivers) {
          const hasAliasForPlatform = driver.aliases.some(alias => 
            alias.platform.toLowerCase() === platform.toLowerCase()
          )
          
          // Add alias names for this platform
          for (const alias of driver.aliases) {
            if (alias.platform.toLowerCase() === platform.toLowerCase() && alias.name) {
              searchableNames.push({
                name: alias.name.trim(),
                driverId: driver.id,
                driverName: driver.primaryName,
                matchedVia: 'alias',
                aliasExists: true,
                needsAlias: false,
                platform: platform
              })
            }
          }
          
          // Add primary name
          searchableNames.push({
            name: driver.primaryName.trim(),
            driverId: driver.id,
            driverName: driver.primaryName,
            matchedVia: 'primary_name',
            aliasExists: hasAliasForPlatform,
            needsAlias: !hasAliasForPlatform
          })
        }

        // Multi-pass name matching for better middle name handling
        return this.performMultiPassNameMatching(searchName, searchableNames)
      }
      
      // No match found
      return null
    } catch (error) {
      console.error('Error searching driver aliases:', error)
      throw new Error('Det gick inte att söka förare')
    }
  }

  /**
   * Multi-pass name matching strategy for handling middle names
   */
  private static performMultiPassNameMatching(
    searchName: string,
    searchableNames: Array<{
      name: string
      driverId: string
      driverName: string
      matchedVia: 'alias' | 'primary_name'
      aliasExists: boolean
      needsAlias: boolean
      platform?: string
    }>
  ) {
    // Pass 2: Normalized First+Last Name Matching (85% threshold)
    const normalizedSearchName = this.extractFirstLastName(searchName)

    for (const candidate of searchableNames) {
      const normalizedCandidate = this.extractFirstLastName(candidate.name)

      if (normalizedSearchName.toLowerCase() === normalizedCandidate.toLowerCase()) {
        return {
          driverId: candidate.driverId,
          driverName: candidate.driverName,
          matchType: 'name' as const,
          matchedVia: candidate.matchedVia,
          matchedValue: candidate.name,
          aliasExists: candidate.aliasExists,
          needsAlias: candidate.needsAlias,
          matchScore: 95,
          fuzzyMatch: false
        }
      }
    }

    // Pass 3: Token-Based Similarity (Jaccard Index, 66% threshold)
    const searchTokens = this.tokenizeName(searchName)
    let bestTokenMatch = { score: 0, candidate: null as any }

    for (const candidate of searchableNames) {
      const candidateTokens = this.tokenizeName(candidate.name)
      const jaccardScore = this.calculateJaccardSimilarity(searchTokens, candidateTokens)

      if (jaccardScore > bestTokenMatch.score && jaccardScore >= 0.70) {
        bestTokenMatch = { score: jaccardScore, candidate }
      }
    }

    if (bestTokenMatch.candidate) {
      return {
        driverId: bestTokenMatch.candidate.driverId,
        driverName: bestTokenMatch.candidate.driverName,
        matchType: 'name' as const,
        matchedVia: bestTokenMatch.candidate.matchedVia,
        matchedValue: bestTokenMatch.candidate.name,
        aliasExists: bestTokenMatch.candidate.aliasExists,
        needsAlias: bestTokenMatch.candidate.needsAlias,
        matchScore: Math.round(bestTokenMatch.score * 100),
        fuzzyMatch: true
      }
    }

    // Pass 4: Enhanced Fuzzy Matching with Name Variants (70% threshold)
    const searchVariants = this.generateNameVariants(searchName)

    for (const searchVariant of searchVariants) {
      // Configure Fuse.js for fuzzy searching on variants
      const fuseOptions = {
        keys: ['name'],
        threshold: 0.3, // 0.3 = 70% similarity threshold
        includeScore: true,
        minMatchCharLength: 2,
        ignoreLocation: true
      }

      const fuse = new Fuse(searchableNames, fuseOptions)
      const fuzzyResults = fuse.search(searchVariant)

      if (fuzzyResults.length > 0) {
        const bestMatch = fuzzyResults[0]
        const matchScore = Math.round((1 - (bestMatch.score || 0)) * 100)

        if (matchScore >= 70) {
          return {
            driverId: bestMatch.item.driverId,
            driverName: bestMatch.item.driverName,
            matchType: 'name' as const,
            matchedVia: bestMatch.item.matchedVia,
            matchedValue: bestMatch.item.name,
            aliasExists: bestMatch.item.aliasExists,
            needsAlias: bestMatch.item.needsAlias,
            matchScore: matchScore,
            fuzzyMatch: true
          }
        }
      }
    }

    return null
  }

  /**
   * Name normalization utilities for improved matching
   */
  private static normalizeNameForMatching(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\p{L}\s]/gu, '') // Remove non-letter characters except spaces
      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
  }

  private static tokenizeName(name: string): string[] {
    return this.normalizeNameForMatching(name)
      .split(' ')
      .filter(token => token.length > 0)
  }

  private static extractFirstLastName(name: string): string {
    const tokens = this.tokenizeName(name)
    if (tokens.length === 0) return ''
    if (tokens.length === 1) return tokens[0]
    if (tokens.length === 2) return tokens.join(' ')

    // For names with 3+ parts, take first and last
    // "Abass Abdirisack Moalin" -> "Abass Moalin"
    return `${tokens[0]} ${tokens[tokens.length - 1]}`
  }

  private static generateNameVariants(name: string): string[] {
    const tokens = this.tokenizeName(name)
    const variants = new Set<string>()

    // Original normalized name
    variants.add(tokens.join(' '))

    // First + Last only (skip middle names)
    if (tokens.length >= 2) {
      variants.add(`${tokens[0]} ${tokens[tokens.length - 1]}`)
    }

    // All consecutive pairs for names with 3+ parts
    if (tokens.length >= 3) {
      for (let i = 0; i < tokens.length - 1; i++) {
        variants.add(`${tokens[i]} ${tokens[i + 1]}`)
      }
    }

    return Array.from(variants)
  }

  private static calculateJaccardSimilarity(tokens1: string[], tokens2: string[]): number {
    const set1 = new Set(tokens1)
    const set2 = new Set(tokens2)

    const intersection = new Set([...set1].filter(x => set2.has(x)))
    const union = new Set([...set1, ...set2])

    return union.size === 0 ? 0 : intersection.size / union.size
  }

  // Subscribe to drivers changes
  static subscribeToDrivers(user: User, callback: (drivers: DriverMaster[]) => void): Unsubscribe {
    try {
      const q = query(
        this.getUserCollection(user),
        where('isActive', '==', true),
        orderBy('primaryName', 'asc')
      )

      return onSnapshot(q, (querySnapshot) => {
        const drivers = querySnapshot.docs.map(doc => this.convertFirestoreDoc(doc))
        callback(drivers)
      }, (error) => {
        console.error('Error in drivers subscription:', error)
        callback([])
      })
    } catch (error) {
      console.error('Error setting up drivers subscription:', error)
      return () => {} // Return empty unsubscribe function
    }
  }

  // Generate merge preview for two drivers
  static async generateMergePreview(user: User, primaryDriverId: string, secondaryDriverId: string): Promise<DriverMergePreview> {
    try {
      const [primaryDriver, secondaryDriver] = await Promise.all([
        this.getDriver(user, primaryDriverId),
        this.getDriver(user, secondaryDriverId)
      ])

      if (!primaryDriver) {
        throw new Error('Primär förare hittades inte')
      }
      if (!secondaryDriver) {
        throw new Error('Sekundär förare hittades inte')
      }
      if (!primaryDriver.isActive || !secondaryDriver.isActive) {
        throw new Error('Båda förarna måste vara aktiva för att kunna slås samman')
      }

      // Count earnings to be transferred
      const secondaryEarnings = await EarningsService.getEarningsByDriver(user, secondaryDriverId)
      const earningsToTransfer = secondaryEarnings.length

      // Identify conflicts
      const conflicts: any = {}
      if (primaryDriver.primaryName !== secondaryDriver.primaryName) {
        conflicts.primaryName = { primary: primaryDriver.primaryName, secondary: secondaryDriver.primaryName }
      }
      if (primaryDriver.email !== secondaryDriver.email && primaryDriver.email && secondaryDriver.email) {
        conflicts.email = { primary: primaryDriver.email, secondary: secondaryDriver.email }
      }
      if (primaryDriver.phone !== secondaryDriver.phone && primaryDriver.phone && secondaryDriver.phone) {
        conflicts.phone = { primary: primaryDriver.phone, secondary: secondaryDriver.phone }
      }
      if (primaryDriver.personalCode !== secondaryDriver.personalCode && primaryDriver.personalCode && secondaryDriver.personalCode) {
        conflicts.personalCode = { primary: primaryDriver.personalCode, secondary: secondaryDriver.personalCode }
      }
      if (primaryDriver.taxiCode !== secondaryDriver.taxiCode && primaryDriver.taxiCode && secondaryDriver.taxiCode) {
        conflicts.taxiCode = { primary: primaryDriver.taxiCode, secondary: secondaryDriver.taxiCode }
      }

      // Merge aliases and identify duplicates
      const primaryPlatforms = new Set(primaryDriver.aliases.map(a => a.platform.toLowerCase()))
      const mergedAliases = [...primaryDriver.aliases]
      const duplicateAliases: Alias[] = []

      for (const alias of secondaryDriver.aliases) {
        if (primaryPlatforms.has(alias.platform.toLowerCase())) {
          duplicateAliases.push(alias)
        } else {
          mergedAliases.push(alias)
        }
      }

      return {
        primaryDriver,
        secondaryDriver,
        conflicts,
        mergedAliases,
        duplicateAliases,
        earningsToTransfer
      }
    } catch (error) {
      console.error('Error generating merge preview:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att generera sammanslagningsförhandsgranskning')
    }
  }

  // Merge two drivers
  static async mergeDrivers(user: User, primaryDriverId: string, secondaryDriverId: string, options: DriverMergeOptions): Promise<DriverMaster> {
    try {
      // Validate input
      if (!options.primaryName?.trim()) {
        throw new Error('Förarens namn är obligatoriskt')
      }
      if (primaryDriverId === secondaryDriverId) {
        throw new Error('Kan inte slå samman en förare med sig själv')
      }

      const result = await runTransaction(db, async (transaction) => {
        // Get both drivers
        const primaryDocRef = this.getDriverDocument(user, primaryDriverId)
        const secondaryDocRef = this.getDriverDocument(user, secondaryDriverId)

        const [primaryDoc, secondaryDoc] = await Promise.all([
          transaction.get(primaryDocRef),
          transaction.get(secondaryDocRef)
        ])

        if (!primaryDoc.exists() || !secondaryDoc.exists()) {
          throw new Error('En eller båda förarna hittades inte')
        }

        const primaryDriver = this.convertFirestoreDoc(primaryDoc)
        const secondaryDriver = this.convertFirestoreDoc(secondaryDoc)

        if (!primaryDriver.isActive || !secondaryDriver.isActive) {
          throw new Error('Båda förarna måste vara aktiva för att kunna slås samman')
        }

        // Merge aliases (avoid platform duplicates)
        const primaryPlatforms = new Set(primaryDriver.aliases.map(a => a.platform.toLowerCase()))
        const mergedAliases = [...primaryDriver.aliases]

        for (const alias of secondaryDriver.aliases) {
          if (!primaryPlatforms.has(alias.platform.toLowerCase())) {
            mergedAliases.push(alias)
          }
        }

        // Prepare merged driver data
        const mergedData: any = {
          primaryName: options.primaryName.trim(),
          aliases: mergedAliases,
          legacyDriverIds: [...primaryDriver.legacyDriverIds, secondaryDriverId, ...secondaryDriver.legacyDriverIds],
          isActive: true,
          updatedAt: serverTimestamp(),
          // Merge optional fields (prefer provided options, then primary, then secondary)
          email: options.email?.trim() || primaryDriver.email || secondaryDriver.email || null,
          phone: options.phone?.trim() || primaryDriver.phone || secondaryDriver.phone || null,
          personalCode: options.personalCode?.trim() || primaryDriver.personalCode || secondaryDriver.personalCode || null,
          taxiCode: options.taxiCode?.trim() || primaryDriver.taxiCode || secondaryDriver.taxiCode || null
        }

        // Update primary driver with merged data
        transaction.update(primaryDocRef, mergedData)

        // Soft delete secondary driver
        transaction.update(secondaryDocRef, {
          isActive: false,
          updatedAt: serverTimestamp()
        })

        // Update all earnings from secondary driver to point to primary driver
        const earningsQuery = query(
          collection(db, 'users', user.uid, 'earnings'),
          where('driverId', '==', secondaryDriverId)
        )

        const earningsSnapshot = await getDocs(earningsQuery)
        for (const earningDoc of earningsSnapshot.docs) {
          const earningRef = doc(db, 'users', user.uid, 'earnings', earningDoc.id)
          transaction.update(earningRef, {
            driverId: primaryDriverId,
            updatedAt: serverTimestamp()
          })
        }

        // Update all compensations from secondary driver to point to primary driver
        const compensationsQuery = query(
          collection(db, 'users', user.uid, 'compensations'),
          where('driverId', '==', secondaryDriverId)
        )

        const compensationsSnapshot = await getDocs(compensationsQuery)
        for (const compensationDoc of compensationsSnapshot.docs) {
          const compensationRef = doc(db, 'users', user.uid, 'compensations', compensationDoc.id)
          transaction.update(compensationRef, {
            driverId: primaryDriverId,
            updatedAt: serverTimestamp()
          })
        }

        return primaryDriverId
      })

      // Get the merged driver
      const mergedDriver = await this.getDriver(user, result)
      if (!mergedDriver) {
        throw new Error('Det gick inte att hämta den sammanslagna föraren')
      }

      return mergedDriver
    } catch (error) {
      console.error('Error merging drivers:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att slå samman förarna')
    }
  }

  // Assign vehicle to driver with transaction (atomic operation)
  static async assignVehicleToDriver(
    user: User,
    vehicleId: string,
    driverId: string | null
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        // Get all drivers to find current assignments
        const driversQuery = query(
          this.getUserCollection(user),
          where('isActive', '==', true)
        )
        const driversSnapshot = await getDocs(driversQuery)

        // Clear vehicle from any other drivers
        for (const driverDoc of driversSnapshot.docs) {
          const driverData = driverDoc.data()
          if (driverData.carId === vehicleId) {
            const driverRef = this.getDriverDocument(user, driverDoc.id)
            transaction.update(driverRef, {
              carId: null,
              updatedAt: serverTimestamp()
            })
          }
        }

        // Assign vehicle to new driver if specified
        if (driverId) {
          // Clear any existing vehicle assignment from this driver
          const targetDriverRef = this.getDriverDocument(user, driverId)
          const targetDriverDoc = await transaction.get(targetDriverRef)

          if (!targetDriverDoc.exists()) {
            throw new Error('Föraren hittades inte')
          }

          transaction.update(targetDriverRef, {
            carId: vehicleId,
            updatedAt: serverTimestamp()
          })
        }
      })
    } catch (error) {
      console.error('Error assigning vehicle to driver:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att tilldela fordonet')
    }
  }

  // Batch update multiple driver-vehicle assignments
  static async batchAssignVehicles(
    user: User,
    assignments: Array<{ vehicleId: string; driverId: string | null }>
  ): Promise<void> {
    if (assignments.length === 0) return

    try {
      await runTransaction(db, async (transaction) => {
        // Get all drivers first
        const driversQuery = query(
          this.getUserCollection(user),
          where('isActive', '==', true)
        )
        const driversSnapshot = await getDocs(driversQuery)
        const allDrivers = driversSnapshot.docs

        // Process each assignment
        for (const assignment of assignments) {
          const { vehicleId, driverId } = assignment

          // Clear vehicle from any current driver
          for (const driverDoc of allDrivers) {
            const driverData = driverDoc.data()
            if (driverData.carId === vehicleId) {
              const driverRef = this.getDriverDocument(user, driverDoc.id)
              transaction.update(driverRef, {
                carId: null,
                updatedAt: serverTimestamp()
              })
            }
          }

          // Assign vehicle to new driver if specified
          if (driverId) {
            const targetDriverRef = this.getDriverDocument(user, driverId)
            const targetDriverDoc = await transaction.get(targetDriverRef)

            if (!targetDriverDoc.exists()) {
              throw new Error(`Föraren med ID ${driverId} hittades inte`)
            }

            // Clear any existing vehicle from this driver
            const currentDriverData = targetDriverDoc.data()
            if (currentDriverData?.carId && currentDriverData.carId !== vehicleId) {
              // This driver had another vehicle, clear it
            }

            transaction.update(targetDriverRef, {
              carId: vehicleId,
              updatedAt: serverTimestamp()
            })
          }
        }
      })
    } catch (error) {
      console.error('Error batch assigning vehicles:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att tilldela fordonen')
    }
  }
}
